import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import logo from '../../assets/pictures/logo.png';

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [loading, setLoading] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const handleSubmit = async e => {
    e.preventDefault();

    if (!agreeTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await signup({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: 'student',
      });

      toast.success('Account created! Complete your onboarding...');
      navigate('/onboarding');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2">
            <a href="https://www.fly8.global/" className="flex items-center">
              <img src={logo} alt="Fly8 Logo" className="h-16 w-auto" />
            </a>
            <Link to="/login">
              <Button
                variant="default"
                className="bg-[#1B86F0] hover:bg-[#1574d0] text-white rounded-full px-6"
              >
                Login
              </Button>
            </Link>
          </div>
        </div>
        <div className="h-1 bg-gradient-to-r from-[#1B86F0] via-[#22A3EA] to-[#1574d0]" />
      </header>

      <div className="flex-1 flex">
        {/* Left Side - Welcome Text with Background */}
        <div
          className="hidden lg:flex flex-1 relative items-center justify-center p-12"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=2070)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* Overlay */}
          <div className="absolute inset-0 opacity-75 bg-gradient-to-br from-[#EAF6FF]/95 via-[#E0F2FF]/90 to-[#D6ECFF]/85" />

          {/* Content */}
          <div className="relative z-10 max-w-lg">
            <h1 className="text-4xl xl:text-5xl font-outfit font-bold text-gray-900 mb-6">
              <span className="text-[#1B86F0]">Register</span> Your Account
            </h1>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Start your global education journey!
            </h2>
            <p className="text-gray-600 leading-relaxed">
              Join thousands who have successfully achieved their international
              education dreams. Create your account today and get access to
              personalized guidance, application tracking, and expert support
              throughout your study abroad journey.
            </p>
          </div>
        </div>

        {/* Right Side - Signup Form */}
        <div className="flex-1 flex items-center justify-center p-8 bg-zinc-50">
          <div className="w-full max-w-md">
            {/* Form Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-outfit font-bold text-[#1B86F0]">
                  Registration
                </h2>
                <p className="text-gray-500 mt-2 text-sm">
                  Create your account to get started.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                data-testid="signup-form"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label
                      htmlFor="firstName"
                      className="text-gray-700 font-medium"
                    >
                      First Name <span className="text-[#1B86F0]">*</span>
                    </Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={e =>
                        setFormData({ ...formData, firstName: e.target.value })
                      }
                      required
                      className="h-12 border-gray-200 focus:border-[#1B86F0] focus:ring-[#1B86F0]/20 rounded-lg"
                      data-testid="signup-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label
                      htmlFor="lastName"
                      className="text-gray-700 font-medium"
                    >
                      Last Name <span className="text-[#1B86F0]">*</span>
                    </Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={e =>
                        setFormData({ ...formData, lastName: e.target.value })
                      }
                      required
                      className="h-12 border-gray-200 focus:border-[#1B86F0] focus:ring-[#1B86F0]/20 rounded-lg"
                      data-testid="signup-lastname-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-medium">
                    Email ID <span className="text-[#1B86F0]">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={e =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    className="h-12 border-gray-200 focus:border-[#1B86F0] focus:ring-[#1B86F0]/20 rounded-lg"
                    data-testid="signup-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="password"
                    className="text-gray-700 font-medium"
                  >
                    Password <span className="text-[#1B86F0]">*</span>
                  </Label>

                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={e =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      required
                      className="h-12 pr-10 border-gray-200 focus:border-[#1B86F0] focus:ring-[#1B86F0]/20 rounded-lg"
                      data-testid="signup-password-input"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label
                    htmlFor="confirmPassword"
                    className="text-gray-700 font-medium"
                  >
                    Confirm Password <span className="text-[#1B86F0]">*</span>
                  </Label>

                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                      className="h-12 pr-10 border-gray-200 focus:border-[#1B86F0] focus:ring-[#1B86F0]/20 rounded-lg"
                      data-testid="signup-confirm-password-input"
                    />

                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                </div>

                <div className="flex items-start space-x-2 pt-2">
                  <Checkbox
                    id="terms"
                    checked={agreeTerms}
                    onCheckedChange={setAgreeTerms}
                    className="mt-1 border-gray-300 data-[state=checked]:bg-[#1B86F0] data-[state=checked]:border-[#1B86F0]"
                  />
                  <Label
                    htmlFor="terms"
                    className="text-sm text-gray-600 font-normal cursor-pointer leading-relaxed"
                  >
                    I agree to the{' '}
                    <Link
                      to="/terms"
                      className="text-[#1B86F0] hover:text-[#1574d0] font-medium"
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      to="/privacy"
                      className="text-[#1B86F0] hover:text-[#1574d0] font-medium"
                    >
                      Privacy Policy
                    </Link>
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-[#1B86F0] hover:bg-[#1574d0] text-white font-semibold rounded-full shadow-lg shadow-[#1B86F0]/25 transition-all duration-200"
                  disabled={loading}
                  data-testid="signup-submit-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              <div className="text-center pt-2">
                <p className="text-sm text-gray-600">
                  Already have an account?{' '}
                  <Link
                    to="/login"
                    className="text-[#1B86F0] hover:text-[#1574d0] font-semibold transition-colors"
                  >
                    Login here
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1B86F0] py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-white text-sm">
            Copyright © {new Date().getFullYear()} Fly8. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
